import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  startOfDay,
  addDays,
  occKey,
  buildOccurrences,
  type Bill,
  type Occurrence,
} from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { CardHeader } from '../components/CardHeader';
import { useScope } from '../hooks/useScope';
import { ThirdPartyLogo } from '../components/ThirdPartyLogos';
import {
  MonthCalendar,
  type CalendarEvent,
  billCategoryLabel,
  fmtCents,
  incomeRingClass,
} from '../components/calendar/MonthCalendar';

// Map a bill's `responsible` value to its person-dot utility —
// dakota blue / natalia pink / shared green.
function billDotClass(bill: Bill): string {
  if (bill.responsible === 'dakota') return 'dot-dakota';
  if (bill.responsible === 'natalia') return 'dot-natalia';
  return 'dot-shared';
}

type RangeMode = 'past' | 'upcoming';

function UpcomingList({
  bills,
  today,
  highlightedKey,
  mode,
  onModeChange,
}: {
  bills: Bill[];
  today: Date;
  highlightedKey: string | null;
  mode: RangeMode;
  onModeChange: (next: RangeMode) => void;
}) {
  // Past covers [today-30, yesterday]; today itself stays in the upcoming
  // window so the "Today" badge isn't surfaced under both views. Past rows
  // sort newest-first (most-recently-due at the top); upcoming sorts soonest.
  // Income occurrences are excluded — paydays read via the calendar's
  // bottom-left dot annotation, not this "amount due" list.
  const billsOnly = useMemo(
    () => bills.filter((b) => b.type !== 'income'),
    [bills],
  );
  const occurrences = useMemo(() => {
    if (mode === 'upcoming') {
      const horizon = addDays(today, 30);
      return buildOccurrences(billsOnly, today, horizon);
    }
    const start = addDays(today, -30);
    const end = addDays(today, -1);
    return buildOccurrences(billsOnly, start, end).reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billsOnly, today.getTime(), mode]);

  return (
    <div className="card-flush">
      <div className="px-5 pt-5 pb-3 max-md:px-4 max-md:pt-4 flex items-start justify-between gap-3 flex-wrap">
        <CardHeader
          title={mode === 'upcoming' ? 'Upcoming' : 'Recent'}
          subtitle={mode === 'upcoming' ? 'Next 30 days' : 'Past 30 days'}
        />
        <div className="scope-toggle" role="tablist">
          <button
            role="tab"
            aria-selected={mode === 'past'}
            onClick={() => onModeChange('past')}
            className={clsx(
              'scope-toggle-btn',
              mode === 'past' && 'scope-toggle-btn-active'
            )}
          >
            Past
          </button>
          <button
            role="tab"
            aria-selected={mode === 'upcoming'}
            onClick={() => onModeChange('upcoming')}
            className={clsx(
              'scope-toggle-btn',
              mode === 'upcoming' && 'scope-toggle-btn-active'
            )}
          >
            Upcoming
          </button>
        </div>
      </div>
      {occurrences.length === 0 ? (
        <div className="px-5 pb-5 max-md:px-4 max-md:pb-4 empty-state">
          {mode === 'upcoming'
            ? 'No bills due in the next 30 days.'
            : 'No bills due in the past 30 days.'}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {occurrences.map((occ, i) => (
            <UpcomingRow
              key={`${occ.bill.name}-${i}`}
              occ={occ}
              today={today}
              highlighted={highlightedKey === occKey(occ)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UpcomingRow({
  occ,
  today,
  highlighted,
}: {
  occ: Occurrence;
  today: Date;
  highlighted: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // When this row becomes the highlighted one, scroll it into view. The flash
  // styling fades on its own when highlighted flips back to false.
  useEffect(() => {
    if (highlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  const days = Math.round((occ.date.getTime() - today.getTime()) / 86_400_000);
  const dayLabel = occ.date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const relative =
    days === 0
      ? 'Today'
      : days === 1
        ? 'Tomorrow'
        : days === -1
          ? 'Yesterday'
          : days > 0
            ? `In ${days} days`
            : `${Math.abs(days)} days ago`;

  return (
    <div
      ref={ref}
      className={clsx(
        'flex items-center gap-3 px-5 py-3 max-md:px-4 transition-colors duration-300',
        // Brief flash on highlight: instant tint that decays back to default
        // when `highlighted` flips false (handled by the parent's setTimeout).
        highlighted
          ? 'bg-green-soft'
          : 'hover:bg-overlay'
      )}
    >
      <span
        className={clsx(
          'w-7 h-7 flex-none flex items-center justify-center rounded-full',
          incomeRingClass(occ.bill),
        )}
      >
        <ThirdPartyLogo location={occ.bill.name} size={24} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate flex items-center gap-2">
          <span
            aria-hidden
            className={clsx('person-dot flex-none', billDotClass(occ.bill))}
          />
          <span className="truncate">{occ.bill.name}</span>
        </div>
        <div className="text-xs text-fg-tertiary truncate">
          {dayLabel}
          {billCategoryLabel(occ.bill) && <> · {billCategoryLabel(occ.bill)}</>}
          {occ.bill.account && <> · {occ.bill.account}</>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5 flex-none">
        {occ.bill.amount > 0 && (
          <div
            className={clsx(
              'tabular text-sm font-semibold',
              occ.bill.type === 'income' && 'delta-pos',
            )}
          >
            {occ.bill.type === 'income' ? '+' : ''}
            {fmtCents(occ.bill.amount)}
          </div>
        )}
        <div className="tabular text-xs text-fg-tertiary">
          {relative}
        </div>
      </div>
    </div>
  );
}

export function BillsPage({
  embedded = false,
  events,
}: {
  embedded?: boolean;
  // Family-calendar events to dot onto the calendar alongside the bills —
  // the home screen's Calendar overlay passes them; the Bills tab doesn't.
  events?: CalendarEvent[];
} = {}) {
  const { data, isLoading, error } = useDashboard();
  const today = useMemo(() => startOfDay(new Date()), []);
  const [cursorMonth, setCursorMonth] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const { scope } = useScope();

  // Per-person scopes filter sole-responsibility bills out and rescale
  // shared bills to that person's share. The split for a shared bill comes
  // from the corresponding Cash Out line item (looked up by the bill's
  // `cashOutItem` reference) — applying the item's dakota/household and
  // natalia/household ratios. If no Cash Out match is found, the full bill
  // amount surfaces on every scope (per user preference — better to over-
  // count than silently hide a bill).
  const allBills = data?.bills.items ?? [];
  const cashOutItemsByName = useMemo(() => {
    const map = new Map<string, { household: number; dakota: number; natalia: number }>();
    for (const c of data?.budget.categories ?? []) {
      for (const item of c.items) {
        if (!item.name) continue;
        map.set(item.name, {
          household: item.household,
          dakota: item.dakota,
          natalia: item.natalia,
        });
      }
    }
    return map;
  }, [data?.budget.categories]);

  const bills = useMemo(() => {
    if (scope === 'household') return allBills;
    return allBills
      .filter((b) => b.responsible === scope || b.responsible === 'shared')
      .map((b) => {
        if (b.responsible === scope) {
          // Sole responsibility: this person owes the full amount.
          return b;
        }
        // Shared bill — look up the Cash Out split.
        const match = b.cashOutItem ? cashOutItemsByName.get(b.cashOutItem) : null;
        if (!match || match.household <= 0) {
          // No linkage or zero-household Cash Out item — fall back to the
          // full amount on every scope rather than silently dropping it.
          return b;
        }
        const ratio =
          scope === 'dakota'
            ? match.dakota / match.household
            : match.natalia / match.household;
        return { ...b, amount: b.amount * ratio };
      });
  }, [allBills, scope, cashOutItemsByName]);

  // Highlight flashes the matching row briefly when a popover bill is
  // clicked. Auto-clears after 700ms so the row tint fades on its own.
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!highlightedKey) return;
    const t = setTimeout(() => setHighlightedKey(null), 700);
    return () => clearTimeout(t);
  }, [highlightedKey]);

  // Range mode for the bills list. Calendar clicks auto-flip the toggle so
  // a past-day click reveals (and highlights) that row in the Past view,
  // and a future-day click reveals it in the Upcoming view.
  const [rangeMode, setRangeMode] = useState<RangeMode>('upcoming');

  const body = (
    <>
      {error && <div className="alert-error">{error}</div>}

      {!data && isLoading && (
        <div className="card text-center py-12 text-fg-secondary">
          Loading…
        </div>
      )}

      {data && bills.length === 0 && (
        <div className="card empty-state">
          No bills in the sheet yet — add a row to the Bills tab with a name,
          frequency, and due day.
        </div>
      )}

      {data && bills.length > 0 && (
        <>
          <MonthCalendar
            bills={bills}
            events={events}
            cursorMonth={cursorMonth}
            today={today}
            onPrev={() =>
              setCursorMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
            }
            onNext={() =>
              setCursorMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))
            }
            onToday={() =>
              setCursorMonth(new Date(today.getFullYear(), today.getMonth(), 1))
            }
            onSelectOccurrence={(occ) => {
              // Flip the list to whichever range contains the clicked
              // occurrence so the highlight has a row to land on. Today
              // belongs to the Upcoming view (matches the row's "Today"
              // label), so the cutoff is strict-less-than today.
              setRangeMode(occ.date.getTime() < today.getTime() ? 'past' : 'upcoming');
              setHighlightedKey(occKey(occ));
            }}
          />
          <UpcomingList
            bills={bills}
            today={today}
            highlightedKey={highlightedKey}
            mode={rangeMode}
            onModeChange={setRangeMode}
          />
        </>
      )}
    </>
  );

  if (embedded) return <div className="flex flex-col gap-8">{body}</div>;

  return (
    <div className="page-container space-y-8">
      <header className="page-header">
        <div>
          <h1 className="page-title">Bills</h1>
          <p className="page-subtitle">
            Recurring bills and one-time payments by due date
          </p>
        </div>
      </header>
      {body}
    </div>
  );
}
